/**
 * CDM Shadow SaaS — Track 2.9.
 *
 * Three-tab view over cdm_saas_apps:
 *   - All apps              every catalogue + observed app
 *   - Shadow SaaS findings  un-sanctioned + seen_in_egress (the action list)
 *   - SSO gaps              sanctioned + seen_in_egress + NOT seen_in_sso
 *
 * The standard CASB pattern: 'in egress NOT in SSO' = shadow IT.
 * Apps are seeded from KNOWN_SAAS_APPS (~150 well-known SaaS); admins
 * sanction / dismiss / add manually.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Cloud, RefreshCw, ChevronLeft, Eye, KeyRound, CheckCircle2,
  AlertTriangle, Shield, Plus, X, Play, Filter,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SaasApp {
  id: string
  canonical_name: string
  domains: string[]
  category: string
  sanctioned: boolean
  sso_provider_app_id: string | null
  seen_in_sso: boolean
  seen_in_egress: boolean
  seen_in_billing: boolean
  egress_user_count: number
  sso_user_count: number
  last_egress_at: string | null
  last_sso_at: string | null
  notes: string | null
  dismissed: boolean
  discovered_at: string | null
  status: 'managed' | 'sso_gap' | 'shadow_saas' | 'unknown' | 'dismissed'
}

interface SaasListResponse {
  count: number
  items: SaasApp[]
  counts: Record<string, number>
}

const STATUS_TONE: Record<string, string> = {
  managed:     'bg-emerald/10 text-emerald border-emerald/30',
  sso_gap:     'bg-amber/10 text-amber-light border-amber/30',
  shadow_saas: 'bg-rose/10 text-rose-light border-rose/30',
  unknown:     'bg-surface-card text-text-muted border-border',
  dismissed:   'bg-surface-card text-text-muted border-border opacity-60',
}

const STATUS_LABEL: Record<string, string> = {
  managed:     'managed',
  sso_gap:     'SSO gap',
  shadow_saas: 'shadow SaaS',
  unknown:     'unknown',
  dismissed:   'dismissed',
}

const CATEGORY_TONE: Record<string, string> = {
  collaboration:  'text-cyan',
  productivity:   'text-blue-light',
  design:         'text-purple-light',
  storage:        'text-emerald',
  crm:            'text-amber-light',
  finance:        'text-emerald',
  devtools:       'text-cyan',
  hr:             'text-purple-light',
  analytics:      'text-blue-light',
  marketing:      'text-amber-light',
  security:       'text-rose-light',
  support:        'text-cyan',
  observability:  'text-blue-light',
  communication:  'text-cyan',
  data:           'text-blue-light',
  identity:       'text-rose-light',
  other:          'text-text-muted',
}

type Mode = 'all' | 'shadow' | 'sso_gaps'

export default function CdmShadowSaasPage() {
  const [data, setData] = useState<SaasListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<Mode>('all')
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [running, setRunning] = useState(false)
  const [discoveryResult, setDiscoveryResult] = useState<any | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/saas-apps')
      .then(r => setData(r.data))
      .catch(() => setData({ count: 0, items: [], counts: {} }))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const filtered = useMemo(() => {
    if (!data) return []
    let items = data.items
    if (mode === 'shadow')   items = items.filter(a => a.status === 'shadow_saas')
    if (mode === 'sso_gaps') items = items.filter(a => a.status === 'sso_gap')
    if (categoryFilter) items = items.filter(a => a.category === categoryFilter)
    if (search) {
      const q = search.toLowerCase()
      items = items.filter(a =>
        a.canonical_name.toLowerCase().includes(q) ||
        a.domains.some(d => d.toLowerCase().includes(q)) ||
        (a.notes || '').toLowerCase().includes(q)
      )
    }
    return items
  }, [data, mode, categoryFilter, search])

  const categories = useMemo(() => {
    if (!data) return []
    return Array.from(new Set(data.items.map(a => a.category))).sort()
  }, [data])

  const triggerDiscovery = () => {
    setRunning(true)
    setDiscoveryResult(null)
    api.post('/api/cdm/saas/discover')
      .then(r => { setDiscoveryResult(r.data); load() })
      .catch(e => setDiscoveryResult({ error: e?.response?.data?.detail || String(e) }))
      .finally(() => setRunning(false))
  }

  const toggleSanctioned = (app: SaasApp) => {
    api.patch(`/api/cdm/saas-apps/${app.id}`, { sanctioned: !app.sanctioned })
      .then(load)
      .catch(() => {})
  }

  const dismiss = (app: SaasApp) => {
    api.patch(`/api/cdm/saas-apps/${app.id}`, { dismissed: !app.dismissed })
      .then(load)
      .catch(() => {})
  }

  const cnt = data?.counts || {}

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Cloud size={18} className="text-cyan" /> SHADOW SAAS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          egress &times; SSO &times; catalogue
        </span>
        <button
          type="button"
          onClick={triggerDiscovery}
          disabled={running}
          className="btn-ghost ml-auto"
          title="Run full discovery: catalogue seed + Entra sign-ins + DNS + flows + reconcile"
        >
          <Play size={11} className={running ? 'animate-pulse' : ''} />
          {running ? 'Running...' : 'Run discovery'}
        </button>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <button type="button" onClick={() => setAddOpen(true)} className="btn-ghost">
          <Plus size={11} /> Add app
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <TabButton
          icon={Cloud}
          label="All apps"
          count={(cnt.managed || 0) + (cnt.sso_gap || 0) + (cnt.shadow_saas || 0) + (cnt.unknown || 0)}
          active={mode === 'all'}
          onClick={() => setMode('all')}
        />
        <TabButton
          icon={Eye}
          label="Shadow SaaS"
          count={cnt.shadow_saas || 0}
          tone="rose"
          active={mode === 'shadow'}
          onClick={() => setMode('shadow')}
        />
        <TabButton
          icon={KeyRound}
          label="SSO gaps"
          count={cnt.sso_gap || 0}
          tone="amber"
          active={mode === 'sso_gaps'}
          onClick={() => setMode('sso_gaps')}
        />
      </div>

      {/* Header rollup */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <RollupTile label="Managed" value={cnt.managed || 0} tone="emerald" icon={CheckCircle2} />
        <RollupTile label="SSO gaps" value={cnt.sso_gap || 0} tone="amber" icon={KeyRound} />
        <RollupTile label="Shadow SaaS" value={cnt.shadow_saas || 0} tone="rose" icon={Eye} />
        <RollupTile label="Unknown" value={cnt.unknown || 0} tone="muted" icon={AlertTriangle} />
        <RollupTile label="Dismissed" value={cnt.dismissed || 0} tone="muted" icon={X} />
      </div>

      {/* Discovery result snippet */}
      {discoveryResult && (
        <div className="card p-3 border-cyan/30 bg-cyan/5">
          <div className="text-[11px] text-cyan font-mono">Discovery complete</div>
          <pre className="text-[10px] text-text-secondary font-mono mt-1 overflow-x-auto">
            {JSON.stringify(discoveryResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Filter bar */}
      <div className="card p-3 flex items-center gap-2 flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name / domain / note..."
          className="input text-[11px] flex-1 min-w-[200px]"
        />
        <select
          className="input text-[11px]"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-[11px] text-text-muted ml-auto">
          {filtered.length} {filtered.length === 1 ? 'app' : 'apps'}
        </span>
      </div>

      {/* Hint by mode */}
      {mode === 'shadow' && filtered.length > 0 && (
        <div className="card p-3 border-rose/30 bg-rose/5 text-[12px] text-text-secondary">
          <strong className="text-rose-light">Action required:</strong>{' '}
          These apps have observed traffic but are not sanctioned and not behind
          SSO. Either sanction (and wire up SSO via Entra), block at the proxy,
          or dismiss as a false positive.
        </div>
      )}
      {mode === 'sso_gaps' && filtered.length > 0 && (
        <div className="card p-3 border-amber/30 bg-amber/5 text-[12px] text-text-secondary">
          <strong className="text-amber-light">SSO gap:</strong>{' '}
          These apps are sanctioned and observed in egress, but not seen in
          Entra sign-in events. Wire them up to Entra SSO so authentication
          becomes the single auditable choke-point.
        </div>
      )}

      {/* Grid of app cards */}
      {loading && (
        <div className="card p-6 text-text-muted text-sm">Loading SaaS apps...</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="card p-6 text-text-muted text-sm text-center">
          {mode === 'all'
            ? 'No SaaS apps yet — click "Run discovery" to seed the catalogue and pull egress / SSO signals.'
            : mode === 'shadow'
              ? 'No shadow SaaS findings — that is a good thing.'
              : 'No SSO gaps — every sanctioned app is properly wired to Entra.'}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(a => (
            <SaasAppCard
              key={a.id}
              app={a}
              onToggleSanctioned={() => toggleSanctioned(a)}
              onDismiss={() => dismiss(a)}
            />
          ))}
        </div>
      )}

      {/* Add manual app modal */}
      {addOpen && (
        <AddSaasAppModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load() }}
        />
      )}

      <p className="text-[10px] text-text-muted text-center pt-4">
        Track 2.9 — standard CASB pattern (egress &times; SSO &times; catalogue) without a CASB.
        Catalogue seeded from KNOWN_SAAS_APPS; admins refine sanctioned status.
      </p>
    </div>
  )
}


// ── Subcomponents ───────────────────────────────────────────────────────────

interface TabButtonProps {
  icon: React.ElementType
  label: string
  count: number
  active: boolean
  onClick: () => void
  tone?: 'rose' | 'amber' | 'cyan'
}

function TabButton({ icon: Icon, label, count, active, onClick, tone }: TabButtonProps) {
  const toneClass =
    tone === 'rose'  ? 'text-rose-light' :
    tone === 'amber' ? 'text-amber-light' :
                       'text-cyan'
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-3 py-1.5 text-[11px] uppercase tracking-widest border-b-2 transition flex items-center gap-1.5',
        active
          ? 'border-cyan text-text-primary'
          : 'border-transparent text-text-muted hover:text-text-primary'
      )}
    >
      <Icon size={12} className={active ? toneClass : ''} />
      <span>{label}</span>
      <span className={clsx(
        'font-mono text-[10px] px-1.5 py-0.5 rounded',
        active ? 'bg-cyan/10 text-cyan' : 'bg-surface-raised text-text-muted'
      )}>
        {count}
      </span>
    </button>
  )
}

interface RollupTileProps {
  label: string
  value: number
  tone: 'emerald' | 'amber' | 'rose' | 'muted'
  icon: React.ElementType
}

function RollupTile({ label, value, tone, icon: Icon }: RollupTileProps) {
  const toneCls =
    tone === 'emerald' ? 'text-emerald' :
    tone === 'amber'   ? 'text-amber-light' :
    tone === 'rose'    ? 'text-rose-light' :
                          'text-text-muted'
  return (
    <div className="card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted">
        <Icon size={10} className={toneCls} /> {label}
      </div>
      <div className={clsx('text-2xl font-black font-mono mt-1', toneCls)}>{value}</div>
    </div>
  )
}

interface SaasAppCardProps {
  app: SaasApp
  onToggleSanctioned: () => void
  onDismiss: () => void
}

function SaasAppCard({ app, onToggleSanctioned, onDismiss }: SaasAppCardProps) {
  const primaryDomain = app.domains[0] || ''
  // Use Clearbit for the logo when we have a primary domain. Failures
  // fall back to a category icon via onError.
  const logoUrl = primaryDomain
    ? `https://logo.clearbit.com/${primaryDomain}?size=64`
    : null
  return (
    <div className={clsx(
      'card p-3 flex flex-col gap-2 border transition',
      app.status === 'shadow_saas' ? 'border-rose/30' :
      app.status === 'sso_gap'     ? 'border-amber/30' :
      app.status === 'managed'     ? 'border-emerald/30' :
      'border-border'
    )}>
      <div className="flex items-start gap-2">
        {logoUrl
          ? <img
              src={logoUrl}
              alt={app.canonical_name}
              className="w-8 h-8 rounded bg-surface-raised flex-shrink-0 object-contain"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          : <Cloud size={32} className={CATEGORY_TONE[app.category] || 'text-text-muted'} />
        }
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-text-primary truncate">
            {app.canonical_name}
          </div>
          <div className={clsx('text-[10px] uppercase tracking-widest', CATEGORY_TONE[app.category] || 'text-text-muted')}>
            {app.category}
          </div>
        </div>
        <span className={clsx(
          'text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-widest',
          STATUS_TONE[app.status] || STATUS_TONE.unknown
        )}>
          {STATUS_LABEL[app.status] || app.status}
        </span>
      </div>

      {/* Domains */}
      <div className="text-[11px] text-text-muted truncate font-mono" title={app.domains.join(', ')}>
        {app.domains.slice(0, 3).join(', ')}
        {app.domains.length > 3 && ` +${app.domains.length - 3}`}
      </div>

      {/* User counts */}
      <div className="flex items-center gap-3 text-[11px] font-mono">
        {app.seen_in_sso && (
          <span className="text-emerald flex items-center gap-1">
            <KeyRound size={10} /> {app.sso_user_count} SSO
          </span>
        )}
        {app.seen_in_egress && (
          <span className="text-amber-light flex items-center gap-1">
            <Eye size={10} /> {app.egress_user_count} egress
          </span>
        )}
        {!app.seen_in_sso && !app.seen_in_egress && (
          <span className="text-text-muted">no observations yet</span>
        )}
      </div>

      {app.notes && (
        <div className="text-[11px] text-text-secondary border-l-2 border-border pl-2 italic">
          {app.notes}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-1 pt-2 border-t border-border">
        <button
          type="button"
          onClick={onToggleSanctioned}
          className={clsx(
            'btn-ghost flex-1 text-[11px]',
            app.sanctioned ? 'text-emerald' : 'text-text-muted'
          )}
          title={app.sanctioned ? 'Mark unsanctioned' : 'Mark sanctioned'}
        >
          <Shield size={11} /> {app.sanctioned ? 'Sanctioned' : 'Sanction'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="btn-ghost text-[11px] text-text-muted"
          title={app.dismissed ? 'Un-dismiss' : 'Dismiss'}
        >
          <X size={11} /> {app.dismissed ? 'Restore' : 'Dismiss'}
        </button>
      </div>
    </div>
  )
}


interface AddSaasAppModalProps {
  onClose: () => void
  onSaved: () => void
}

function AddSaasAppModal({ onClose, onSaved }: AddSaasAppModalProps) {
  const [canonicalName, setCanonicalName] = useState('')
  const [domains, setDomains] = useState('')
  const [category, setCategory] = useState('other')
  const [sanctioned, setSanctioned] = useState(false)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = () => {
    if (!canonicalName.trim()) { setError('Canonical name required'); return }
    setSaving(true)
    setError(null)
    api.post('/api/cdm/saas-apps', {
      canonical_name: canonicalName.trim(),
      domains: domains.split(',').map(d => d.trim()).filter(Boolean),
      category,
      sanctioned,
      notes: notes.trim() || null,
    })
      .then(() => onSaved())
      .catch(e => setError(e?.response?.data?.detail || String(e)))
      .finally(() => setSaving(false))
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="card p-4 max-w-md w-full space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">Add SaaS app</h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </div>
        <p className="text-[11px] text-text-muted">
          Add an app not in the canonical catalogue. Domains are how the
          discovery engine matches egress traffic.
        </p>
        <div className="space-y-2">
          <label className="block text-[10px] uppercase tracking-widest text-text-muted">Canonical name</label>
          <input
            type="text"
            value={canonicalName}
            onChange={e => setCanonicalName(e.target.value)}
            placeholder="e.g. AcmeBank Trade Surveillance"
            className="input w-full text-[12px]"
          />
          <label className="block text-[10px] uppercase tracking-widest text-text-muted">Domains (comma-separated)</label>
          <input
            type="text"
            value={domains}
            onChange={e => setDomains(e.target.value)}
            placeholder="acmesurveil.com, app.acmesurveil.com"
            className="input w-full text-[12px] font-mono"
          />
          <label className="block text-[10px] uppercase tracking-widest text-text-muted">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="input w-full text-[12px]"
          >
            {['collaboration','productivity','design','storage','crm','finance','devtools','hr','analytics','marketing','security','support','observability','communication','data','identity','other'].map(c =>
              <option key={c} value={c}>{c}</option>
            )}
          </select>
          <label className="flex items-center gap-2 text-[12px] text-text-secondary">
            <input
              type="checkbox"
              checked={sanctioned}
              onChange={e => setSanctioned(e.target.checked)}
            />
            Sanctioned (bank-approved use)
          </label>
          <label className="block text-[10px] uppercase tracking-widest text-text-muted">Notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Why this app is here, license owner, contact, etc."
            className="input w-full text-[12px]"
          />
        </div>
        {error && <p className="text-[11px] text-rose-light">{error}</p>}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="btn-ghost text-[11px] text-cyan"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
